/**
 * StockPhotoPicker — modal for searching Unsplash / Pexels stock photos.
 *
 * Uses backend `/api/stock-photos/search` which proxies to Unsplash/Pexels
 * (when API keys are configured) or a curated fallback bank.
 *
 * Usage:
 *   <StockPhotoPicker open={open} onClose={close} onPick={(url)=>{...}} />
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Search, X, Loader2, ExternalLink, Camera } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const SUGGESTED_QUERIES = [
  'nature', 'business', 'food', 'fashion', 'people',
  'art', 'landscape', 'wedding', 'technology', 'abstract',
];

export default function StockPhotoPicker({ open, onClose, onPick }) {
  const [query, setQuery] = useState('');
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sourceName, setSourceName] = useState('');
  const [note, setNote] = useState('');

  const search = useCallback(async (q) => {
    setLoading(true);
    setPhotos([]);
    try {
      const r = await axios.get(`${API}/stock-photos/search`, { params: { q, per_page: 30 } });
      setPhotos(r.data.photos || []);
      setSourceName(r.data.source || '');
      setNote(r.data.note || '');
    } catch {
      toast.error('Search failed');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) search('');
  }, [open, search]);

  const submit = (e) => {
    e?.preventDefault();
    search(query);
  };

  const pick = (photo) => {
    onPick?.(photo.url);
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(photo.url).catch(() => {});
      }
    } catch { /* clipboard denied — silently ignore */ }
    toast.success('Photo added');
    onClose?.();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose} data-testid="stock-photo-modal">
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-4xl max-h-[90vh] flex flex-col rounded-2xl border border-white/10 bg-[#171A2C] shadow-2xl overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-white/8 shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500 to-violet-500 flex items-center justify-center">
              <Camera size={18} className="text-white" />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#A78BFA]">Free Stock Photos</p>
              <h3 className="text-white font-bold text-lg font-syne">Search millions of photos</h3>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-white/50 hover:text-white hover:bg-white/5" data-testid="stock-close">
            <X size={16} />
          </button>
        </div>

        {/* Search */}
        <form onSubmit={submit} className="p-5 border-b border-white/8 shrink-0">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Try 'coffee', 'wedding', 'landscape'..."
                className="w-full pl-9 pr-3 py-2.5 rounded-lg bg-white/5 border border-white/10 text-white text-sm placeholder-white/30 focus:border-[#A78BFA]/50 outline-none"
                data-testid="stock-search-input"
              />
            </div>
            <button
              type="submit"
              className="px-5 py-2.5 rounded-lg bg-gradient-to-r from-[#8B5CF6] to-[#6D28D9] text-white text-xs font-bold uppercase tracking-wider hover:shadow-[0_8px_20px_rgba(139,92,246,0.4)]"
              data-testid="stock-search-submit"
            >
              Search
            </button>
          </div>
          <div className="mt-3 flex flex-wrap gap-1.5">
            {SUGGESTED_QUERIES.map((q) => (
              <button
                key={q}
                type="button"
                onClick={() => { setQuery(q); search(q); }}
                className="px-2.5 py-1 rounded-full bg-white/5 hover:bg-white/10 border border-white/8 text-white/70 text-[11px] transition-all"
                data-testid={`stock-tag-${q}`}
              >
                {q}
              </button>
            ))}
          </div>
          {note && <p className="mt-2 text-[10px] text-amber-300/80">{note}</p>}
          {sourceName && !note && <p className="mt-2 text-[10px] text-white/40">via {sourceName}</p>}
        </form>

        {/* Grid */}
        <div className="p-5 overflow-y-auto flex-1">
          {loading && (
            <div className="flex items-center justify-center gap-2 py-16 text-white/50">
              <Loader2 size={16} className="animate-spin" /> Searching…
            </div>
          )}
          {!loading && photos.length === 0 && (
            <div className="text-center py-16 text-white/40">
              <Camera size={28} className="mx-auto mb-2 opacity-40" />
              <p className="text-sm">Try a search term above</p>
            </div>
          )}
          {!loading && photos.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3" data-testid="stock-photos-grid">
              {photos.map((p, i) => (
                <div key={p.id || i} className="group relative aspect-[4/3] rounded-lg overflow-hidden border border-white/8 hover:border-[#A78BFA]/50 transition-all cursor-pointer" onClick={() => pick(p)} data-testid={`stock-photo-${i}`}>
                  <img
                    src={p.thumb || p.url}
                    alt={p.alt}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-end p-2">
                    <p className="text-[10px] text-white/85 truncate">{p.author}</p>
                    <button
                      onClick={(e) => { e.stopPropagation(); pick(p); }}
                      className="mt-1 px-2 py-1 rounded bg-[#A78BFA] text-[#0E0E23] text-[10px] font-bold uppercase tracking-wider hover:bg-white"
                    >
                      Use photo
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-white/8 flex items-center justify-between text-[10px] text-white/40 shrink-0">
          <span>Free to use for commercial and personal projects</span>
          <a href="https://unsplash.com/license" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:text-white">
            License <ExternalLink size={10} />
          </a>
        </div>
      </div>
    </div>
  );
}
